// User types
export type Permission = string;
export type DefaultRole = 'admin' | 'agent' | 'user';

// Module definitions
export type ModuleId = 'tickets' | 'chat' | 'admin' | 'analytics' | 'settings' | 'desktop_releases';

export interface Module {
    id: ModuleId;
    name: string;
    description: string;
    isSystem?: boolean; // If true, cannot be disabled purely by role (e.g. settings might be base)
}

export const APP_MODULES: Module[] = [
    { id: 'tickets', name: 'Ticketing System', description: 'Access to ticket management and viewing.' },
    { id: 'chat', name: 'Team Chat', description: 'Access to groups and direct messages.' },
    { id: 'admin', name: 'Admin Dashboard', description: 'Access to system configuration and user management.' },
    { id: 'analytics', name: 'Analytics', description: 'View system usage and performance metrics.' },
    { id: 'settings', name: 'Personal Settings', description: 'Access to profile and preferences.', isSystem: true },
    { id: 'desktop_releases', name: 'Desktop Releases', description: 'Manage and deploy desktop application updates.' }
];

export interface Role {
    id: string;
    name: string;
    description?: string;
    permissions: Permission[];
    allowed_modules: ModuleId[];
    created_at: string;
}

export interface GlobalSettings {
    default_self_destruct_timer: number;
    allow_user_custom_timer: boolean;
    allow_multiple_assignees: boolean;
    enable_read_receipts?: boolean;
    require_invite_acceptance?: boolean;
    max_file_size_mb?: number;
    allowed_file_types?: string[]; // 'image', 'video', 'document', 'audio', 'archive'
    disable_emojis?: boolean;

    // Storage
    storage_provider?: 'local' | 's3';
    s3_bucket?: string;
    s3_region?: string;
    s3_endpoint?: string;
    s3_access_key?: string;
    s3_secret_key?: string;
    s3_ignore_ssl?: boolean;

    // Sensitive Data Warnings
    warn_on_credit_card?: boolean;
    warn_on_ssn?: boolean;
    warn_on_password?: boolean;

    // Ticket Settings
    activity_log_visibility?: 'all' | 'department_heads' | 'admins';
    available_tags?: string[];
    next_ticket_number?: number;

    // Email/SMTP Settings
    email_provider?: 'smtp' | 'resend' | 'gmail';  // Default: smtp
    resend_api_key?: string;  // For Resend provider
    smtp_host?: string;
    smtp_port?: number;
    smtp_user?: string;
    smtp_password?: string;
    smtp_secure?: boolean;  // SSL/TLS connection
    smtp_from_email?: string;
    smtp_from_name?: string;

    // 2FA Settings
    require_2fa?: boolean;
    require_oauth_recovery?: boolean;  // If true, users must link Google/Microsoft before accessing the app
    whitelisted_ips?: string[];  // IPs that skip 2FA

    // User Settings
    default_role_id?: string;  // Default role assigned to new users
    allow_profile_edit?: boolean;
    self_destruct_behavior?: 'delete' | 'vanish';
    max_self_destruct_retention?: number;

    // File Retention Policy
    file_retention_enabled?: boolean;
    file_size_threshold_mb?: number;
    large_file_retention_days?: number;
    small_file_retention_days?: number;
    large_file_retention_options?: number[];
    small_file_retention_options?: number[];
    show_retention_warning?: boolean;
    allow_user_never_delete?: boolean;
    max_upload_size_mb?: number;
    small_file_cleanup_interval_hours?: number;
    large_file_cleanup_interval_hours?: number;
    small_file_grace_period_hours?: number;
    large_file_grace_period_hours?: number;
    small_file_cleanup_active?: boolean;
    large_file_cleanup_active?: boolean;
    retention_cleanup_interval_hours?: number;
    retention_grace_period_hours?: number;
    retention_notify_before_days?: number;
    enable_grace_period?: boolean;
    enable_expiry_notifications?: boolean;
    enable_file_deduplication?: boolean;
    enable_image_compression?: boolean;
    image_compression_quality?: number;
    allow_user_disable_compression?: boolean;
    file_retention_cleanup_active?: boolean;

    // Encryption Policy
    encryption_policy?: {
        channel_mode?: EncryptionMode;
        dm_mode?: EncryptionMode;
        topic_mode?: EncryptionMode;
    };

    // Desktop App settings
    desktop_sync_window_days?: number;
    desktop_attachment_sync_days?: number;
    desktop_max_messages_channel?: number;
    desktop_allow_attachments?: boolean;
    app_url?: string; // Base URL for absolute links in emails
    isDbConnected?: boolean;

    // Unread message email notifications
    enable_unread_email_notifications?: boolean;
}

export interface FileRecord {
    id: string;
    key: string;
    original_name: string;
    mime_type: string;
    size_bytes: number;
    uploaded_by: string;
    uploaded_at: string;
    storage_provider: 'local' | 's3';
    message_id: string | null;
    channel_id: string | null;
    retention_days: number;
    never_delete: boolean;
    expires_at: string | null;
    deleted_at: string | null;
    file_hash: string | null;
    is_compressed: boolean;
    original_size_bytes: number;
    url?: string;
}

export interface User {
    id: string;
    name: string;
    email: string;
    avatar: string | null;
    status: 'active' | 'away' | 'offline' | 'deleted';
    status_type?: 'online' | 'away' | 'dnd' | 'lunch' | 'vacation' | 'offline';
    status_text?: string;
    last_active_at?: number;
    department_id?: string;
    department_ids?: string[];  // Multiple departments support
    role_id?: string;
    role?: Role;
    public_key?: string;
    enc_private_key?: string;
    key_salt?: string;
    preferences?: {
        self_destruct_timer?: number;
        send_delay?: number | null; // null = disabled, 1-5 = seconds. Default behaviour is 3
        unread_email_enabled?: boolean;
        unread_email_delay_minutes?: number;
    };
    created_at: string;
    // Auth fields
    password_hash?: string;        // null = needs password setup
    password_set_at?: string;
    invite_token?: string;
    invite_expires_at?: string;
    invite_status?: 'pending' | 'accepted';
    reset_token?: string;
    reset_expires_at?: string;
    two_fa_enabled?: boolean;
    enc_private_key_passkey?: string;  // Set when user has linked an OAuth recovery account
}

// Department types
export interface Department {
    id: string;
    name: string;
    color: string;
    head_ids?: string[];  // Users who head this department
    allow_external_individual_assignment?: boolean;  // Can external users assign to individuals? Default: false (only dept/head)
}

// Encryption modes
export type EncryptionMode = 'optional' | 'forced' | 'disabled';

// Group types
export interface Channel {
    id: string;
    name: string;
    is_private: boolean;
    created_by: string;
    created_at: string;
    member_ids: string[];
    pending_invites?: string[];
    encryption_mode?: EncryptionMode;
    allow_member_invites?: boolean;
    last_message_at?: string;
    is_archived?: boolean;
    invite_status?: 'member' | 'pending' | 'none';
    topics?: Topic[];
}

// Direct Message types
export interface DirectMessage {
    id: string;
    participant_ids: string[];
    participants?: User[];
    last_message_at?: string;
    created_at: string;
    encryption_mode?: EncryptionMode;
}

// Topic types - short-term collaboration groups

// Topic types - short-term collaboration groups
export interface Topic {
    id: string;
    name: string;
    description?: string;
    color?: string;
    created_by: string;
    created_at: string;
    member_ids: string[];
    pending_invites?: string[];
    expires_at?: string;
    last_message_at?: string;
    is_archived?: boolean;
    encryption_mode?: EncryptionMode;
    allow_member_invites?: boolean;
    invite_status?: 'member' | 'pending' | 'none';
    members?: User[];
    pending_members?: User[];
}

// Message types
export interface Message {
    id: string;
    content: string; // Encrypted blob if is_encrypted is true
    author_id: string;
    author?: User;
    channel_id?: string | null;
    dm_id?: string | null;
    ticket_id?: string | null;
    topic_id?: string | null;
    parent_id?: string | null;
    created_at: string;
    reply_count?: number;
    source?: any;
    sourceType?: 'channel' | 'dm' | 'ticket' | 'topic';

    // E2EE fields
    is_encrypted?: boolean;
    is_broadcast?: boolean;
    // Map of user_id -> encrypted_message_key (Base64)
    // This encrypted_message_key allows the user to decrypt the content
    encrypted_keys?: Record<string, string>;

    // Self Destruct fields
    self_destruct?: boolean;
    expires_at?: string; // ISO string
    duration?: number; // Duration in seconds

    read_by?: { user_id: string; read_at: string }[];
    attachments?: { id: string; name: string; type: string; size: number; url: string }[];
    reactions?: Record<string, string[]>;
    unread_by?: string[];
    snoozed_by?: { user_id: string; until: string }[]; // Array of users who snoozed this message
    scheduled_at?: string; // ISO string for scheduled send

    // Edit/Delete fields
    is_edited?: boolean;
    is_deleted?: boolean;
    edited_at?: string;
    deleted_at?: string;
}

export interface Subtask {
    id: string;
    title: string;
    completed: boolean;
}

// Activity Log Entry
export type ActivityAction = 'created' | 'status_changed' | 'priority_changed' | 'assigned' | 'unassigned' |
    'collaborator_added' | 'collaborator_removed' | 'due_date_set' | 'tag_added' | 'tag_removed' | 'edited' | 'comment' | 'deleted';

export interface ActivityEntry {
    id: string;
    action: ActivityAction;
    user_id: string;
    user?: User;
    timestamp: string;
    details: {
        from?: string;
        to?: string;
        field?: string;
        value?: string;
        user_name?: string;
    };
}

// Ticket types
export type TicketStatus = 'open' | 'in_progress' | 'resolved' | 'closed' | 'deleted';
export type TicketPriority = 'low' | 'medium' | 'high' | 'urgent';
export type WorkItemType = 'ticket' | 'task' | 'bug' | 'request' | 'incident' | 'approval' | 'milestone';
export type ProjectPrivacy = 'private' | 'department' | 'organization';
export type ProjectMemberRole = 'owner' | 'editor' | 'viewer';
export type ProjectCustomFieldType = 'text' | 'number' | 'date' | 'single_select' | 'multi_select' | 'checkbox' | 'person';

export interface ProjectSection {
    id: string;
    project_id: string;
    name: string;
    position: number;
    created_at: string;
}

export interface ProjectMember {
    project_id: string;
    user_id: string;
    role: ProjectMemberRole;
    added_at: string;
    user?: User;
}

export interface ProjectCustomField {
    id: string;
    project_id: string;
    name: string;
    field_type: ProjectCustomFieldType;
    config: { options?: string[]; [key: string]: unknown };
    position: number;
    is_required: boolean;
    created_at: string;
}

export interface ProjectTicketMembership {
    project_id: string;
    name: string;
    color: string;
    section_id?: string | null;
    position: number;
}

export interface Project {
    id: string;
    name: string;
    description: string;
    color: string;
    department_id?: string | null;
    owner_id: string;
    privacy: ProjectPrivacy;
    item_visibility: 'participants' | 'project_members';
    is_archived: boolean;
    created_at: string;
    updated_at: string;
    members: ProjectMember[];
    sections: ProjectSection[];
    custom_fields: ProjectCustomField[];
    ticket_ids: string[];
    ticket_count: number;
    permissions: {
        can_edit_items: boolean;
        can_manage: boolean;
        member_role: ProjectMemberRole | null;
    };
}

export interface CreateProjectData {
    name: string;
    description?: string;
    color?: string;
    department_id?: string | null;
    privacy?: ProjectPrivacy;
    item_visibility?: 'participants' | 'project_members';
    member_ids?: string[];
}

export interface ProjectBulkChanges {
    status?: TicketStatus;
    priority?: TicketPriority;
    due_date?: string | null;
    assignee_ids?: string[];
    section_id?: string | null;
}

export interface Ticket {
    id: string;
    ticket_number: number;      // Auto-increment starting at 100
    title: string;
    description: string;
    status: TicketStatus;
    priority: TicketPriority;
    item_type?: WorkItemType;
    start_date?: string | null;
    estimate_minutes?: number | null;
    parent_id?: string | null;
    is_milestone?: boolean;
    version_no?: number;
    department_id?: string | null;
    department?: Department;
    assigned_to_department_id?: string | null;  // For dept queue: ticket assigned to dept, not individual
    created_by: string;
    created_by_user?: User;
    created_at: string;
    due_date?: string;          // ISO date string
    tags?: string[];            // Array of tag names
    assignees?: User[];
    collaborators?: User[];
    subtasks?: Subtask[];
    activity_log?: ActivityEntry[];
    attachments?: any[];
    deleted_at?: string | null;
    projects?: ProjectTicketMembership[];
    custom_fields?: Record<string, unknown>;
}

// Thread types
export interface ThreadData {
    parent: Message;
    replies: Message[];
}

// View types
export type ViewType = 'channel' | 'dm' | 'ticket' | 'topic' | 'tickets-list' | 'inbox' | 'admin' | 'ticket-calendar' | 'ticket-dashboard' | 'ticket-board' | 'project-list' | 'dept-queue';

export interface ActiveView {
    type: ViewType;
    id: string;
}

// API request/response types
export interface CreateTicketData {
    title: string;
    description?: string;
    priority?: TicketPriority;
    department_id?: string | null;
    assigned_to_department_id?: string | null;
    assignee_ids?: string[];
    collaborator_ids?: string[];
    due_date?: string | null;
    tags?: string[];
    attachments?: any[];
    item_type?: WorkItemType;
    start_date?: string | null;
    estimate_minutes?: number | null;
    parent_id?: string | null;
    is_milestone?: boolean;
    project_id?: string | null;
    section_id?: string | null;
}

export interface UpdateTicketData {
    title?: string;
    description?: string;
    status?: TicketStatus;
    priority?: TicketPriority;
    department_id?: string | null;
    assigned_to_department_id?: string | null;  // For dept queue assignment
    assignee_ids?: string[];
    collaborator_ids?: string[];
    subtasks?: Subtask[];
    due_date?: string | null;
    tags?: string[];
    attachments?: any[];
    item_type?: WorkItemType;
    start_date?: string | null;
    estimate_minutes?: number | null;
    parent_id?: string | null;
    is_milestone?: boolean;
}

export interface CreateDepartmentData {
    name: string;
    color?: string;
}

export interface UpdateUserData {
    name?: string;
    email?: string;
    avatar?: string | null;
    status?: 'active' | 'away' | 'offline';
    status_type?: 'online' | 'away' | 'dnd' | 'lunch' | 'vacation' | 'offline';
    status_text?: string;
    department_id?: string | null;
    role_id?: string | null;
    public_key?: string;
    preferences?: {
        self_destruct_timer?: number;
        send_delay?: number | null;
        unread_email_enabled?: boolean;
        unread_email_delay_minutes?: number;
    };
}

export interface SendMessageData {
    content: string;
    channel_id?: string;
    dm_id?: string;
    ticket_id?: string;
    parent_id?: string;
    is_encrypted?: boolean;
    encrypted_keys?: Record<string, string>;
    self_destruct?: boolean;
    duration?: number;
    attachments?: { id?: string; name: string; type: string; size: number; url: string }[];
}
